import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const REFS = [
  { key: 'agencija101',  logo: '/logos/agencija101.png',   name: 'Agencija 101 d.o.o.',                    color: '#E4003B', hasBg: true  },
  { key: 'lasko',        logo: '/logos/lasko-union.svg',   name: 'Pivoarna Laško Union d.o.o.',            color: '#003087', hasBg: true,  makeBlack: true },
  { key: 'cocacola',     logo: '/logos/coca-cola-hbc.svg', name: 'Coca Cola HBC Slovenija d.o.o.',         color: '#F40000', hasBg: true,  makeBlack: true },
  { key: 'vita',         logo: '/logos/vita.png',          name: 'Zavarovalnica Vita, d.d.',               color: '#5B2D8E', hasBg: true  },
  { key: 'eurospin',     logo: '/logos/eurospin.png',      name: 'EUROSPIN EKO, d.o.o.',                   color: '#1565C0', hasBg: true  },
  { key: 'kpk',          logo: '/logos/kpk.svg',           name: 'Komisija za preprečevanje korupcije',    color: '#003DA5', hasBg: true,  makeBlack: true },
];

const ACCENT = '#C87533';

export const ReferencesSection = () => {
  const sectionRef = useRef(null);
  const dekraRef   = useRef(null);
  const pmiRef     = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).team;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {

      // ── Entrance animations (once) ──
      gsap.fromTo('.ref-header',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.7,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 75%', once: true } }
      );
      gsap.fromTo('.ref-card',
        { opacity: 0, y: 30, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power3.out', stagger: 0.08,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true } }
      );

      // ── Scroll scrub: DEKRA exits → PMI slides left ──
      gsap.set(pmiRef.current, { opacity: 0.45 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.2,
          invalidateOnRefresh: true,
          onRefresh: () => {
            // Recalculate distance on every refresh/resize
            const dx = dekraRef.current.getBoundingClientRect().left
                     - pmiRef.current.getBoundingClientRect().left;
            gsap.set(pmiRef.current, { x: 0 });
            tl.vars._dx = dx;
          },
        },
      });

      const dx = dekraRef.current.getBoundingClientRect().left
               - pmiRef.current.getBoundingClientRect().left;

      tl.to({}, { duration: 0.4 })
        .to(dekraRef.current, { opacity: 0, y: -20, duration: 0.2 })
        .to(pmiRef.current,   { opacity: 1, x: dx,  duration: 0.25 }, '-=0.15')
        .to({}, { duration: 0.25 });

    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="references"
      className="scroll-section relative"
      style={{ minHeight: '200vh' }}
    >
      {/* Sticky viewport */}
      <div className="sticky top-0 min-h-screen flex items-center px-4 py-20">
        <div className="section-content max-w-[1100px] mx-auto w-full">

          {/* Header */}
          <div className="ref-header text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
              <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
                {tx.refsLabel}
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {lang === 'sl'
                ? <><span className="text-[#C87533]">Izkušnje</span>, ki govorijo same.</>
                : <><span className="text-[#C87533]">Experience</span> that speaks for itself.</>
              }
            </h2>
            <p className="text-white/45 max-w-lg mx-auto text-base leading-relaxed">
              {lang === 'sl'
                ? 'Naša ekipa je sodelovala z nekaterimi največjimi blagovnimi znamkami v Sloveniji in regiji.'
                : 'Our team has partnered with some of the largest brands in Slovenia and the region.'}
            </p>
          </div>

          {/* Logo grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {REFS.map((ref) => (
              <div
                key={ref.key}
                className="ref-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col items-center gap-4 hover:border-white/15 hover:bg-white/5 transition-all duration-300 cursor-default"
                style={{ '--ref-color': ref.color }}
              >
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${ref.color}18 0%, transparent 70%)` }}
                />
                <div className="relative w-full h-14 flex items-center justify-center">
                  {ref.hasBg ? (
                    <div className="bg-white rounded-xl px-3 py-2 flex items-center justify-center w-full h-full overflow-hidden opacity-85 group-hover:opacity-100 transition-opacity duration-300">
                      <img
                        src={ref.logo}
                        alt={ref.name}
                        className="max-w-full max-h-9 object-contain"
                        style={ref.makeBlack ? { filter: 'brightness(0)' } : {}}
                      />
                    </div>
                  ) : (
                    <img
                      src={ref.logo}
                      alt={ref.name}
                      className="max-w-full max-h-12 object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ filter: 'brightness(1.1) saturate(0.85)' }}
                    />
                  )}
                </div>
                <p className="text-white/40 text-xs text-center font-mono leading-relaxed group-hover:text-white/65 transition-colors duration-300">
                  {ref.name}
                </p>
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full"
                  style={{ background: ref.color }}
                />
              </div>
            ))}

            {/* DEKRA — exits on scroll */}
            <div
              ref={dekraRef}
              className="ref-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col items-center gap-4 cursor-default"
            >
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 0%, #009A4418 0%, transparent 70%)' }} />
              <div className="relative w-full h-14 flex items-center justify-center">
                <div className="bg-white rounded-xl px-3 py-2 flex items-center justify-center w-full h-full overflow-hidden opacity-85">
                  <img src="/logos/dekra.png" alt="DEKRA" className="max-w-full max-h-9 object-contain" />
                </div>
              </div>
              <p className="text-white/40 text-xs text-center font-mono leading-relaxed">
                DEKRA ZAPOSLITEV d.o.o. (JTI)
              </p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full bg-[#009A44]" />
            </div>

            {/* PMI — slides left to DEKRA's position */}
            <div
              ref={pmiRef}
              className="ref-card group relative rounded-2xl backdrop-blur-sm p-6 flex flex-col items-center gap-4 cursor-default"
              style={{ border: `1px solid ${ACCENT}40`, background: `${ACCENT}0A`, boxShadow: `0 0 32px ${ACCENT}18` }}
            >
              <div className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${ACCENT}18 0%, transparent 70%)` }} />
              <div className="relative w-full h-14 flex items-center justify-center">
                <img
                  src="/pmi-logo.png"
                  alt="Philip Morris International"
                  className="max-h-12 max-w-full object-contain"
                  onError={e => { e.currentTarget.style.display='none'; e.currentTarget.nextSibling.style.display='flex'; }}
                />
                <div className="hidden w-full h-full items-center justify-center">
                  <span className="font-mono font-bold text-[#C87533] text-lg tracking-widest">PMI</span>
                </div>
              </div>
              <p className="text-[#C87533]/70 text-xs text-center font-mono leading-relaxed">
                Philip Morris International
              </p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-2/3 rounded-full" style={{ background: ACCENT }} />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
